import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Users } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

const CalendarView = ({ monthlyCalendar, currentMonth, currentYear, onChangeMonth }) => {
  // State for tooltip
  const [tooltipData, setTooltipData] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  // Holiday type colors
  const holidayColors = {
    national: { bg: 'bg-red-100', border: 'border-red-400', text: 'text-red-700', badge: 'bg-red-200' },
    regional: { bg: 'bg-orange-100', border: 'border-orange-400', text: 'text-orange-700', badge: 'bg-orange-200' },
    local_ciudad_real: { bg: 'bg-purple-100', border: 'border-purple-400', text: 'text-purple-700', badge: 'bg-purple-200' },
    local_bolanos: { bg: 'bg-blue-100', border: 'border-blue-400', text: 'text-blue-700', badge: 'bg-blue-200' },
    local_almagro: { bg: 'bg-emerald-100', border: 'border-emerald-400', text: 'text-emerald-700', badge: 'bg-emerald-200' },
    local_pozuelo: { bg: 'bg-amber-100', border: 'border-amber-400', text: 'text-amber-700', badge: 'bg-amber-200' },
    custom_global: { bg: 'bg-indigo-100', border: 'border-indigo-400', text: 'text-indigo-700', badge: 'bg-indigo-200' },
    custom: { bg: 'bg-slate-100', border: 'border-slate-400', text: 'text-slate-700', badge: 'bg-slate-200' }
  };

  // Get color for holiday type (handles custom_ prefixes)
  const getHolidayColor = (type) => {
    if (holidayColors[type]) {
      return holidayColors[type];
    }
    // Handle custom_local_* types
    if (type && type.startsWith('custom_local_')) {
      return { bg: 'bg-teal-100', border: 'border-teal-400', text: 'text-teal-700', badge: 'bg-teal-200' };
    }
    if (type && type.startsWith('custom')) {
      return holidayColors.custom_global;
    }
    return { bg: 'bg-gray-100', border: 'border-gray-400', text: 'text-gray-700', badge: 'bg-gray-200' };
  };

  // Show tooltip with all employees on vacation
  const handleShowTooltip = (e, vacations, dayNumber, holidayName) => {
    if (vacations.length === 0) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const containerRect = e.currentTarget.closest('.calendar-container')?.getBoundingClientRect() || { left: 0, top: 0 };
    
    setTooltipPosition({
      x: rect.left - containerRect.left + rect.width / 2,
      y: rect.bottom - containerRect.top + 5
    });
    
    setTooltipData({
      day: dayNumber,
      vacations: vacations,
      holiday: holidayName
    });
  };

  const handleHideTooltip = () => {
    setTooltipData(null);
  };

  // Helper function to format date as YYYY-MM-DD
  const formatDateKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const renderCalendarDays = () => {
    if (!monthlyCalendar) return null;

    const startDate = new Date(monthlyCalendar.year, currentMonth - 1, 1);
    const endDate = new Date(monthlyCalendar.year, currentMonth, 0);
    const firstDayOfWeek = (startDate.getDay() + 6) % 7;
    const daysInMonth = endDate.getDate();
    
    const days = [];
    
    // Empty cells for days before month start
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push(
        <div key={`empty-${i}`} className="p-2 h-24 border border-slate-200 rounded bg-slate-50">
        </div>
      );
    }
    
    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dayDate = new Date(monthlyCalendar.year, currentMonth - 1, day);
      const dayKey = formatDateKey(dayDate);
      const vacationsToday = monthlyCalendar.vacation_days[dayKey] || [];
      const holidayToday = monthlyCalendar.holidays?.[dayKey];
      const isToday = dayDate.toDateString() === new Date().toDateString();
      const isWeekend = dayDate.getDay() === 0 || dayDate.getDay() === 6;
      
      // Determine background based on priority
      let bgClass = 'bg-white';
      let borderClass = 'border-slate-200';
      
      if (holidayToday) {
        const colors = getHolidayColor(holidayToday.type);
        bgClass = colors.bg;
        borderClass = colors.border;
      } else if (vacationsToday.length > 0) {
        bgClass = 'bg-sky-100';
        borderClass = 'border-sky-300';
      } else if (isWeekend) {
        bgClass = 'bg-slate-50';
      }
      
      days.push(
        <div 
          key={day} 
          className={`p-1 h-24 border rounded relative ${bgClass} ${borderClass} ${isToday ? 'ring-2 ring-blue-500' : ''} ${vacationsToday.length > 0 ? 'cursor-pointer' : ''}`}
          onMouseEnter={(e) => vacationsToday.length > 0 && handleShowTooltip(e, vacationsToday, day, holidayToday?.name)}
          onMouseLeave={handleHideTooltip}
        >
          <div className={`text-sm font-medium ${isToday ? 'text-blue-700' : isWeekend ? 'text-slate-500' : 'text-slate-800'}`}>
            {day}
          </div>
          
          {/* Holiday indicator */}
          {holidayToday && (
            <div 
              className={`text-xs font-medium px-1 py-0.5 rounded truncate mt-0.5 ${getHolidayColor(holidayToday.type).badge} ${getHolidayColor(holidayToday.type).text}`}
              title={holidayToday.name}
            >
              {holidayToday.name.length > 12 ? holidayToday.name.substring(0, 10) + '...' : holidayToday.name}
            </div>
          )}
          
          {/* Vacations - simplified display with hover for details */}
          {vacationsToday.length > 0 && (
            <div className="absolute bottom-1 left-1 right-1 overflow-hidden">
              <div className="flex items-center justify-center gap-1">
                <Users className="h-3 w-3 text-sky-600" />
                <span className="text-xs font-semibold text-sky-700 bg-sky-200 px-1.5 py-0.5 rounded">
                  {vacationsToday.length} {vacationsToday.length === 1 ? 'persona' : 'personas'}
                </span>
              </div>
            </div>
          )}
        </div>
      );
    }
    
    return days;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Calendario de Vacaciones y Festivos</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => onChangeMonth(-1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-lg font-semibold min-w-[140px] text-center">
              {monthlyCalendar ? `${monthlyCalendar.month_name} ${monthlyCalendar.year}` : ''}
            </span>
            <Button variant="outline" size="sm" onClick={() => onChangeMonth(1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {monthlyCalendar ? (
          <div className="space-y-4">
            {/* Calendar Grid with tooltip container */}
            <div className="calendar-container relative">
              <div className="grid grid-cols-7 gap-1 text-center">
                {/* Week headers - Starting Monday */}
                {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map((day) => (
                  <div key={day} className="p-2 text-sm font-medium text-slate-600 bg-slate-100 rounded">
                    {day}
                  </div>
                ))}
                
                {/* Calendar days */}
                {renderCalendarDays()}
              </div>
              
              {/* Tooltip for vacation details */}
              {tooltipData && (
                <div 
                  className="absolute z-50 bg-white border border-slate-200 rounded-lg shadow-xl p-3 min-w-[200px] max-w-[280px] transform -translate-x-1/2"
                  style={{ 
                    left: tooltipPosition.x, 
                    top: tooltipPosition.y
                  }}
                  onMouseEnter={() => {}} // Keep tooltip visible when hovering over it
                  onMouseLeave={handleHideTooltip}
                >
                  {/* Tooltip arrow */}
                  <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-b-8 border-l-transparent border-r-transparent border-b-white"></div>
                  <div className="absolute -top-2.5 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-b-8 border-l-transparent border-r-transparent border-b-slate-200"></div>
                  
                  {/* Header */}
                  <div className="flex items-center gap-2 mb-2 pb-2 border-b border-slate-100">
                    <Users className="h-4 w-4 text-sky-600" />
                    <span className="font-semibold text-slate-800">
                      Día {tooltipData.day} - {tooltipData.vacations.length} {tooltipData.vacations.length === 1 ? 'persona' : 'personas'}
                    </span>
                  </div>
                  
                  {/* Holiday info if any */}
                  {tooltipData.holiday && (
                    <div className="text-xs bg-amber-50 text-amber-700 px-2 py-1 rounded mb-2">
                      🎉 {tooltipData.holiday}
                    </div>
                  )}
                  
                  {/* List of employees */}
                  <div className="space-y-1.5 max-h-[200px] overflow-y-auto">
                    {tooltipData.vacations.map((vacation, index) => (
                      <div 
                        key={index}
                        className="flex items-center justify-between text-sm bg-sky-50 rounded px-2 py-1.5"
                      >
                        <div className="flex flex-col">
                          <span className="font-medium text-slate-800">{vacation.user_name}</span>
                          <span className="text-xs text-slate-500">{vacation.department}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* Legend */}
            <div className="flex flex-wrap items-center gap-3 text-sm p-3 bg-slate-50 rounded-lg">
              <span className="font-medium text-slate-700">Leyenda:</span>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 bg-white border border-slate-200 rounded"></div>
                <span className="text-slate-600">Normal</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 bg-sky-100 border border-sky-300 rounded"></div>
                <span className="text-slate-600">Vacaciones</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 bg-red-100 border border-red-400 rounded"></div>
                <span className="text-slate-600">Nacional</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 bg-orange-100 border border-orange-400 rounded"></div>
                <span className="text-slate-600">Autonómico</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 bg-indigo-100 border border-indigo-400 rounded"></div>
                <span className="text-slate-600">Global (Todos)</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 bg-purple-100 border border-purple-400 rounded"></div>
                <span className="text-slate-600">Ciudad Real</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 bg-blue-100 border border-blue-400 rounded"></div>
                <span className="text-slate-600">Bolaños</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 bg-emerald-100 border border-emerald-400 rounded"></div>
                <span className="text-slate-600">Almagro</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 bg-amber-100 border border-amber-400 rounded"></div>
                <span className="text-slate-600">Pozuelo</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 bg-white border-2 border-blue-500 rounded"></div>
                <span className="text-slate-600">Hoy</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-slate-500">Cargando calendario...</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default CalendarView;
